// Engineer Layout - Mobile-first layout with bottom navigation
import { Link, useLocation, useParams } from 'react-router-dom'
import { Home, Search, ShoppingCart, ClipboardList, MoreHorizontal } from 'lucide-react'
import { useCart } from '../../auth/hooks/useCart'
import { HOME_TAB_ENABLED } from '../../config/features'
import { EngineerHeader } from './EngineerHeader'

interface EngineerLayoutProps {
  children: React.ReactNode
  hideBottomNav?: boolean
  /** Hide the shared top header — used on JobSetup, which IS the postcode
   * entry screen and so shouldn't repeat the pill. (DEV-48214 Q8) */
  hideHeader?: boolean
}

export function EngineerLayout({
  children,
  hideBottomNav = false,
  hideHeader = false,
}: EngineerLayoutProps) {
  const location = useLocation()
  const { tenantSlug } = useParams()

  // Use backend cart instead of sessionStorage
  const { cart } = useCart()
  const cartCount = cart.length

  // Home tab (DEV-46921) is behind the HOME_TAB_ENABLED build flag —
  // omit the item entirely when the flag is off so the nav falls back
  // to the pre-DEV-46921 four-tab layout (Search / Cart / Orders / More).
  const navItems = [
    ...(HOME_TAB_ENABLED
      ? [
          {
            name: 'Home',
            href: `/${tenantSlug}/engineer/home`,
            icon: Home,
          },
        ]
      : []),
    {
      name: 'Search',
      href: `/${tenantSlug}/engineer/search`,
      icon: Search,
    },
    {
      name: 'Basket',
      href: `/${tenantSlug}/engineer/cart`,
      icon: ShoppingCart,
    },
    {
      name: 'Orders',
      href: `/${tenantSlug}/engineer/orders`,
      icon: ClipboardList,
    },
    {
      name: 'More',
      href: `/${tenantSlug}/engineer/more`,
      icon: MoreHorizontal,
    },
  ]

  // Active-tab detection. Home also owns the Favourites and
  // Recently-Purchased subpages (they're reached via "View All" from
  // the Home carousels — DEV-46921), so the Home tab stays highlighted
  // on those.
  const isActive = (href: string) => {
    if (href.endsWith('/home')) {
      const base = `/${tenantSlug}/engineer`
      return (
        location.pathname.startsWith(`${base}/home`) ||
        location.pathname.startsWith(`${base}/favourites`) ||
        location.pathname.startsWith(`${base}/recently-purchased`)
      )
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Shared engineer header (postcode pill + weekly-spend gauge). Hidden on
       * JobSetup since that page IS the postcode-entry flow. Per-page sticky
       * bars in engineer pages remain — they render below this header. */}
      {!hideHeader && <EngineerHeader />}

      {/* Main Content */}
      <main className={`flex-1 ${!hideBottomNav ? 'pb-20' : ''}`}>
        {children}
      </main>

      {/* Bottom Navigation. Colours are driven by the tenant's Nav Background /
       * Nav Text theme (DEV-48214). Active items use full `--nav-text`;
       * inactive items use `--nav-text-inactive` (mid-value derived from nav
       * text + bg so contrast holds on any admin-picked palette). */}
      {!hideBottomNav && (
        <nav className="fixed bottom-0 left-0 right-0 bg-nav border-t border-nav-divider safe-area-bottom z-40">
          <div className="flex items-center justify-around h-16">
            {navItems.map((item) => {
              const active = isActive(item.href)
              const Icon = item.icon

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors relative ${
                    active ? 'text-nav' : 'text-nav-inactive'
                  }`}
                >
                  <div className="relative">
                    <Icon className={`w-6 h-6 ${active ? 'stroke-[2.5]' : ''}`} />
                    {/* Basket Badge — hardcoded red per Q4; universal "alert" colour. */}
                    {item.name === 'Basket' && cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {cartCount > 9 ? '9+' : cartCount}
                      </span>
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${active ? 'font-medium' : ''}`}>
                    {item.name}
                  </span>
                </Link>
              )
            })}
          </div>
        </nav>
      )}
    </div>
  )
}
